<style lang="scss" src='./SensorForecast.scss' scoped></style>
<template src="./SensorForecast.html"></template>

<script>
import ApexChart from 'vue-apexcharts';
import { getSensorGraph } from '@/api/index';

import moment from 'moment';
import messages from './SensorForecast.i18n';

// Forecast readings. Each names its arrays in the forecast response: `fc` the expected daily
// average, `lo`/`hi` the 80% likely range, `lo50`/`hi50` the 50% range, `dry` the "if not irrigated"
// line (only for sensors flagged as irrigated). Ranges are drawn only when the response carries them. temp_f is °F
// and ec is dS/m, the same units as the history endpoint. `maxDays` caps how far ahead a reading
// is shown: soil temperature beats "same as today" only for about a week (fast-forward replay,
// reports/sensor-forecast).
const PARAMS = [
  {
    key: 'vwc', label: 'alerts.Moisture', uom: 'MOISTURE', color: '#1E86C4', decimals: 1,
    fc: 'vwc', lo: 'vwc_lo', hi: 'vwc_hi', lo50: 'vwc_lo50', hi50: 'vwc_hi50', dry: 'vwc_dry',
  },
  {
    key: 'temperature', label: 'alerts.Temperature', uom: 'TEMP', color: '#F5842A', decimals: 1, maxDays: 7,
    fc: 'temp_f', lo: 'temp_lo', hi: 'temp_hi', lo50: 'temp_lo50', hi50: 'temp_hi50',
  },
  {
    key: 'bulk_ec', label: 'alerts.Salinity', uom: 'SALINITY', color: '#0FA79B', decimals: 2,
    fc: 'ec', lo: 'ec_lo', hi: 'ec_hi', lo50: 'ec_lo50', hi50: 'ec_hi50',
  },
];

// Days ahead -> days of measured readings shown before them.
const HORIZONS = [
  { days: 3, context: 3 },
  { days: 7, context: 5 },
  { days: 14, context: 7 },
];

const MEASURED_COLOR = '#33434A';
const DRY_COLOR = '#8A979C';
const RAIN_COLOR = '#0A5E86';
const ET_COLOR = '#F5A623';

export default {
  name: 'SensorForecast',
  i18n: { messages },
  props: {
    sensor: {
      required: false,
    },
    forecast: {
      required: false,
    },
  },
  components: {
    ApexChart,
  },
  data() {
    return ({
      params: PARAMS,
      selectedParam: 'vwc',
      selectedHorizon: 7,
      granularity: 'daily',
      openDay: null,
      visible: false,
      history: [],
      loading: false,
    });
  },
  computed: {
    param() {
      return PARAMS.find((p) => p.key === this.selectedParam);
    },

    // Horizons offered for the selected reading.
    horizons() {
      return HORIZONS.filter((h) => !this.param.maxDays || h.days <= this.param.maxDays);
    },

    horizon() {
      return this.horizons.find((h) => h.days === this.selectedHorizon) || this.horizons[this.horizons.length - 1];
    },

    imperial() {
      const course = this.G_getFromStore('course');
      return !course || course.measurement_system !== 'METRIC';
    },

    forecastOk() {
      return !!(this.forecast && this.forecast.status === 'ok' && this.forecast.dates);
    },

    // Forecast days in the selected horizon, with every value converted to the course's units.
    days() {
      if (!this.forecastOk) {
        return [];
      }
      const fc = this.forecast;
      const p = this.param;
      const at = (field, k) => (field && fc[field] ? this.toNumber(p, fc[field][k]) : null);
      return fc.dates.slice(0, this.horizon.days).map((date, k) => ({
        date,
        x: this.noon(date),
        y: at(p.fc, k),
        lo: at(p.lo, k),
        hi: at(p.hi, k),
        lo50: at(p.lo50, k),
        hi50: at(p.hi50, k),
        dry: at(p.dry, k),
        rain: fc.rain_mm ? fc.rain_mm[k] : null,
        rainProb: fc.rain_prob ? fc.rain_prob[k] : null,
        et: fc.et0_mm ? fc.et0_mm[k] : null,
      }));
    },

    // Hourly detail for the next 72 hours, when the forecast carries it.
    hasHourly() {
      const h = this.forecastOk && this.forecast.hourly;
      return !!(h && h.start && h[this.param.fc]);
    },

    hourlyMode() {
      return this.granularity === 'hourly' && this.hasHourly;
    },

    hourlyPoints() {
      if (!this.hasHourly) {
        return [];
      }
      const h = this.forecast.hourly;
      const p = this.param;
      const t0 = new Date(h.start).getTime();
      const at = (field, k) => (field && h[field] ? this.toNumber(p, h[field][k]) : null);
      return h[p.fc].map((_, k) => {
        const x = t0 + k * 3600 * 1000;
        return {
          x,
          date: moment(x).format('YYYY-MM-DD'),
          hour: moment(x).format('HH:mm'),
          y: at(p.fc, k),
          lo: at(p.lo, k),
          hi: at(p.hi, k),
          lo50: at(p.lo50, k),
          hi50: at(p.hi50, k),
        };
      });
    },

    // Measured hourly readings for the 48 hours before the hourly forecast starts.
    measuredHourly() {
      if (!this.hourlyPoints.length) {
        return [];
      }
      const p = this.param;
      const t0 = this.hourlyPoints[0].x;
      return this.history
        .filter((r) => r[p.key] !== null && r[p.key] !== undefined && r[p.key] !== '')
        .map((r) => ({ x: new Date(r.reading_time).getTime(), y: this.toNumber(p, r[p.key]) }))
        .filter((pt) => pt.x >= t0 - 48 * 3600 * 1000 && pt.x < t0)
        .sort((a, b) => a.x - b.x);
    },

    // Forecast hours of one day, for the expanded row in the day-by-day table.
    hoursOf() {
      const byDay = {};
      this.hourlyPoints.forEach((pt) => {
        (byDay[pt.date] = byDay[pt.date] || []).push(pt);
      });
      return byDay;
    },

    hasRange() {
      return this.days.some((d) => d.lo !== null && d.hi !== null);
    },

    hasRange50() {
      return this.days.some((d) => d.lo50 !== null && d.hi50 !== null);
    },

    hasDry() {
      return this.days.some((d) => d.dry !== null);
    },


    // Measured daily averages for the context days before the forecast starts.
    measured() {
      if (!this.forecastOk) {
        return [];
      }
      const p = this.param;
      const first = this.forecast.dates[0];
      const from = moment(first).subtract(this.horizon.context, 'days').format('YYYY-MM-DD');
      const byDay = {};
      this.history.forEach((r) => {
        const v = r[p.key];
        if (v === null || v === undefined || v === '') {
          return;
        }
        const day = moment(r.reading_time).format('YYYY-MM-DD');
        if (day < from || day >= first) {
          return;
        }
        (byDay[day] = byDay[day] || []).push(Number(v));
      });
      return Object.keys(byDay).sort().map((day) => {
        const vals = byDay[day];
        return { x: this.noon(day), y: this.toNumber(p, vals.reduce((a, b) => a + b, 0) / vals.length) };
      });
    },

    chartSeries() {
      const p = this.param;
      const hourly = this.hourlyMode;
      const days = hourly ? this.hourlyPoints : this.days;
      const measured = hourly ? this.measuredHourly : this.measured;
      const last = measured.length ? measured[measured.length - 1] : null;
      // Forecast lines and ranges start from the last measured day so they read as one curve.
      const join = (pts) => (last ? [{ x: last.x, y: last.y }, ...pts] : pts);
      const joinRange = (pts) => (last ? [{ x: last.x, y: [last.y, last.y] }, ...pts] : pts);
      const series = [];
      const dot = hourly ? 0 : 4;
      if (days.some((d) => d.lo !== null && d.hi !== null)) {
        series.push({
          name: this.$t('sf.range80'), type: 'rangeArea', color: p.color, width: 0, dash: 0, opacity: 0.22, marker: 0,
          data: joinRange(days.filter((d) => d.lo !== null && d.hi !== null).map((d) => ({ x: d.x, y: [d.lo, d.hi] }))),
        });
      }
      if (days.some((d) => d.lo50 !== null && d.hi50 !== null)) {
        series.push({
          name: this.$t('sf.range50'), type: 'rangeArea', color: p.color, width: 0, dash: 0, opacity: 0.3, marker: 0,
          data: joinRange(days.filter((d) => d.lo50 !== null && d.hi50 !== null).map((d) => ({ x: d.x, y: [d.lo50, d.hi50] }))),
        });
      }
      series.push({
        name: this.$t(hourly ? 'sf.measuredHourly' : 'sf.measured'), type: 'line', color: MEASURED_COLOR, width: 2, dash: 0, opacity: 1, marker: dot,
        data: measured,
      });
      series.push({
        name: this.$t(hourly ? 'sf.forecastHourly' : 'sf.forecast'), type: 'line', color: p.color, width: hourly ? 2 : 3, dash: 6, opacity: 1, marker: dot,
        data: join(days.filter((d) => d.y !== null).map((d) => ({ x: d.x, y: d.y }))),
      });
      if (!hourly && this.hasDry) {
        series.push({
          name: this.$t('sf.ifNotIrrigated'), type: 'line', color: DRY_COLOR, width: 2, dash: 2, opacity: 1, marker: 0,
          data: join(days.filter((d) => d.dry !== null).map((d) => ({ x: d.x, y: d.dry }))),
        });
      }
      return series;
    },

    chartOptions() {
      const p = this.param;
      const series = this.chartSeries;
      const unit = this.unit(p);
      const yAnnotations = [];
      const threshold = this.sensor && this.sensor.threshold;
      if (threshold && (p.key === 'vwc' || p.key === 'temperature')) {
        const field = p.key === 'vwc' ? 'moisture' : 'temperature';
        ['min', 'max'].forEach((side) => {
          const val = threshold[`${side}_${field}`];
          if (val !== null && val !== undefined && val !== '') {
            const y = this.toNumber(p, val);
            yAnnotations.push({
              y,
              borderColor: '#9AA7AC',
              strokeDashArray: 4,
              label: { text: `${side === 'min' ? 'Min' : 'Max'} ${y}${unit}`, style: { color: '#5C6B70', background: 'transparent' }, borderWidth: 0 },
            });
          }
        });
      }
      const hourly = this.hourlyMode;
      const days = hourly ? this.hourlyPoints : this.days;
      const xAnnotations = days.length ? [{
        x: hourly ? days[0].x : new Date(`${days[0].date}T00:00:00`).getTime(),
        x2: days[days.length - 1].x + (hourly ? 0 : 12 * 3600 * 1000),
        fillColor: '#DCE8EE',   // drawn over the series, so keep it faint
        opacity: 0.25,
        borderColor: '#5C6B70',
        label: {
          text: this.$t('sf.forecastRegion'),
          orientation: 'horizontal',
          position: 'top',
          textAnchor: 'start',
          style: { color: '#fff', background: '#5C6B70' },
        },
      }] : [];
      // Fit the y axis to the readings, ranges and thresholds (a range chart otherwise starts at 0).
      const ys = [...yAnnotations.map((a) => a.y)];
      series.forEach((s) => s.data.forEach((pt) => (Array.isArray(pt.y) ? ys.push(...pt.y) : ys.push(pt.y))));
      const finite = ys.filter((y) => y !== null && Number.isFinite(y));
      const axis = finite.length ? this.niceAxis(Math.min(...finite), Math.max(...finite)) : {};
      return {
        chart: {
          type: 'rangeArea',
          height: 340,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: series.map((s) => s.color),
        stroke: { curve: 'straight', width: series.map((s) => s.width), dashArray: series.map((s) => s.dash) },
        fill: { opacity: series.map((s) => s.opacity) },
        markers: { size: series.map((s) => s.marker), strokeWidth: 0, hover: { sizeOffset: 2 } },
        dataLabels: { enabled: false },
        legend: { show: true, position: 'top', horizontalAlign: 'left' },
        grid: { borderColor: '#E4EAEA', strokeDashArray: 0 },
        xaxis: {
          type: 'datetime',
          labels: { datetimeUTC: false, format: hourly ? 'ddd HH:mm' : 'MMM dd', style: { colors: '#5C6B70' } },
          axisBorder: { color: '#E4EAEA' },
          tooltip: { enabled: false },
        },
        yaxis: {
          decimalsInFloat: p.decimals,
          min: axis.min,
          max: axis.max,
          tickAmount: axis.ticks,
          title: { text: unit, style: { color: '#5C6B70', fontWeight: 400 } },
          labels: { style: { colors: '#5C6B70' } },
        },
        annotations: { yaxis: yAnnotations, xaxis: xAnnotations },
        tooltip: {
          shared: true,
          intersect: false,
          x: { format: hourly ? 'ddd, MMM dd HH:mm' : 'ddd, MMM dd' },
          y: { formatter: (v) => (v === null || v === undefined ? '' : `${Number(v).toFixed(p.decimals)} ${unit}`) },
        },
        noData: { text: this.loading ? this.$t('sf.loading') : this.$t('sf.noData') },
      };
    },

    // Forecast days already in the past: the sensor's readings lag behind (data_lag_days), so those
    // days are estimates for a period without readings.
    pastDates() {
      const fc = this.forecast;
      const lag = (this.forecastOk && fc.data_lag_days) || 0;
      return new Set((fc && fc.dates ? fc.dates : []).slice(0, lag));
    },

    // Day-by-day rows under the chart.
    rows() {
      const p = this.param;
      const unit = this.unit(p);
      const fmt = (n) => (n === null ? '–' : Number(n).toFixed(p.decimals));
      const depthUnit = this.depthUnit();
      return this.days.map((d) => ({
        date: d.date,
        day: this.day(d.date),
        past: this.pastDates.has(d.date),
        hours: (this.hoursOf[d.date] || []).map((h) => ({
          hour: h.hour,
          expected: h.y === null ? '–' : `${fmt(h.y)} ${unit}`,
          range: h.lo !== null && h.hi !== null ? `${fmt(h.lo)} – ${fmt(h.hi)} ${unit}` : '–',
        })),
        expected: d.y === null ? '–' : `${fmt(d.y)} ${unit}`,
        range: d.lo !== null && d.hi !== null ? `${fmt(d.lo)} – ${fmt(d.hi)} ${unit}` : '–',
        dry: d.dry === null ? '–' : `${fmt(d.dry)} ${unit}`,
        rain: d.rain === null ? '–'
          : `${this.depth(d.rain)} ${depthUnit}${d.rainProb !== null && d.rainProb !== undefined ? ` (${d.rainProb}%)` : ''}`,
        et: d.et === null ? '–' : `${this.depth(d.et)} ${depthUnit}`,
      }));
    },

    // Same window as the chart: observed context days, then the forecast days.
    weatherDays() {
      if (!this.forecastOk || !this.forecast.hist_dates) {
        return [];
      }
      const fc = this.forecast;
      const n = this.horizon.context;
      const past = fc.hist_dates.map((d, k) => ({ date: d, rain: fc.hist_rain_mm[k], et: fc.hist_et0_mm[k], forecast: false }))
        .slice(-n);
      const next = this.days.map((d) => ({ date: d.date, rain: d.rain, et: d.et, forecast: true }));
      return [...past, ...next];
    },

    weatherSeries() {
      return [
        { name: this.$t('sf.rain'), data: this.weatherDays.map((d) => this.depth(d.rain)) },
        { name: this.$t('sf.et0'), data: this.weatherDays.map((d) => this.depth(d.et)) },
      ];
    },

    weatherOptions() {
      const days = this.weatherDays;
      const labels = days.map((d) => moment(d.date).format('MMM D'));
      const firstForecast = days.findIndex((d) => d.forecast);
      const unit = this.depthUnit();
      return {
        chart: { type: 'bar', height: 170, toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: 'inherit' },
        colors: [RAIN_COLOR, ET_COLOR],
        plotOptions: { bar: { columnWidth: '70%', borderRadius: 2 } },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        dataLabels: { enabled: false },
        legend: { show: true, position: 'top', horizontalAlign: 'left' },
        grid: { borderColor: '#E4EAEA' },
        xaxis: {
          categories: labels,
          labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: '#5C6B70' } },
          axisBorder: { color: '#E4EAEA' },
        },
        yaxis: {
          decimalsInFloat: this.imperial ? 2 : 1,
          title: { text: unit, style: { color: '#5C6B70', fontWeight: 400 } },
          labels: { style: { colors: '#5C6B70' } },
        },
        annotations: {
          xaxis: firstForecast >= 0 ? [{
            x: labels[firstForecast],
            x2: labels[labels.length - 1],
            fillColor: '#DCE8EE',
            opacity: 0.25,
            label: { text: this.$t('sf.forecastRegion'), orientation: 'horizontal', style: { color: '#5C6B70', background: 'transparent' }, borderWidth: 0 },
          }] : [],
        },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v === null || v === undefined ? '' : `${v} ${unit}`) } },
      };
    },

    // Plain-language outlook lines for the moisture forecast.
    outlook() {
      if (!this.forecastOk) {
        return [];
      }
      const fc = this.forecast;
      const lines = [];
      if (fc.data_lag_days > 0) {
        lines.push({ icon: 'clock-history', tone: 'warn', text: this.$t('sf.lagging', { date: this.day(fc.base_day) }) });
      }
      if (fc.deficit_pts !== null && fc.deficit_pts !== undefined && fc.deficit_pts >= 0.5) {
        lines.push({ icon: 'droplet-half', text: this.$t('sf.deficit', { pts: fc.deficit_pts.toFixed(1), usual: fc.usual_wet_vwc }) });
      }
      const sum = (arr) => (arr || []).slice(0, 7).reduce((a, b) => a + (b || 0), 0);
      const unit = this.depthUnit();
      lines.push({
        icon: 'cloud-rain',
        text: this.$t('sf.water7', { rain: `${this.depth(sum(fc.rain_mm))} ${unit}`, et: `${this.depth(sum(fc.et0_mm))} ${unit}` }),
      });
      if (fc.vwc_dry) {
        const min = fc.vwc_min_threshold;
        if (min && fc.below_min_date_dry && fc.below_min_date_dry !== 'now') {
          lines.push({ icon: 'droplet', text: this.$t('sf.belowOnDry', { min, date: this.day(fc.below_min_date_dry) }) });
        } else if (min && !fc.below_min_date_dry) {
          lines.push({ icon: 'droplet', text: this.$t('sf.staysAboveDry', { min }) });
        }
        const why = this.irrigationWhy(fc.irrigation);
        if (why) {
          lines.push({ icon: 'calendar-check', text: why });
        }
      } else if (fc.irrigation_days) {
        lines.push({ icon: 'calendar-check', text: this.$t('sf.risesWithoutRain', { n: fc.irrigation_days }) });
      }
      if (fc.batt_days_to_low) {
        lines.push({ icon: 'battery-half', tone: 'warn', text: this.$t('sf.battLow', { days: fc.batt_days_to_low }) });
      }
      return lines;
    },

    learning() {
      if (!this.forecast) {
        return null;
      }
      const fc = this.forecast;
      const lr = fc.learning || {};
      const h = 2;   // 3 days ahead
      let track = this.$t('sf.trackPending');
      if (lr.mae_forecast && lr.mae_forecast[h] !== null && lr.mae_forecast[h] !== undefined
          && lr.mae_same_as_today && lr.mae_same_as_today[h] !== null && lr.mae_same_as_today[h] !== undefined) {
        track = this.$t('sf.trackRecord', {
          n: lr.n[h], fc: Number(lr.mae_forecast[h]).toFixed(1), ps: Number(lr.mae_same_as_today[h]).toFixed(1),
        });
      }
      let band = this.$t('sf.bandNone');
      if (this.hasRange) {
        band = lr.band_source === 'live'
          ? this.$t('sf.bandLive', { n: lr.n ? lr.n[h] : 0 })
          : this.$t('sf.bandHindcast');
      }
      const next = fc.next_tier;
      return {
        tier: fc.tier || 'insufficient',
        cleanDays: fc.clean_days || 0,
        progress: Math.min(100, Math.round(((fc.clean_days || 0) / 60) * 100)),
        need: next ? this.$t('sf.tierNeed', { days: next.days_needed, tier: this.$t(`sf.tier.${next.tier}`) }) : this.$t('sf.tierMature'),
        track: this.forecastOk ? track : null,
        band: this.forecastOk ? band : null,
        updated: fc.issued_at ? this.$t('sf.updated', { when: this.G_formatDate({ date: fc.issued_at, pretty: false }) }) : null,
        reason: fc.status === 'skipped' ? this.$t(`sf.reason.${fc.reason}`) : null,
      };
    },
  },
  watch: {
    // switching to a reading with a shorter limit moves the selection to its longest horizon
    selectedParam() {
      this.selectedHorizon = this.horizon.days;
      if (!this.hasHourly) {
        this.granularity = 'daily';
      }
    },
    sensor: function (newSensor, oldSensor) {
      if (newSensor && oldSensor && newSensor.sensor_id !== oldSensor.sensor_id) {
        this.loadHistory();
      }
    },
  },

  methods: {
    // Value in the course's units, as a number.
    toNumber(p, val) {
      if (val === null || val === undefined || val === '') {
        return null;
      }
      if (p.uom === 'TEMP') {   // the shared converter rounds to whole degrees; keep a decimal for charts
        const course = this.G_getFromStore('course');
        const f = Number(val);
        const t = course && course.measurement_system === 'METRIC' ? ((f - 32) * 5) / 9 : f;
        return Number(t.toFixed(p.decimals));
      }
      const n = this.G_getMeasurement({ val, uom: this.consts.UOM[p.uom], addUnit: false, invertUOM: false });
      return n === '' ? null : Number(Number(n).toFixed(p.decimals));
    },

    // Axis bounds on a round step (1, 2, 5 x 10^n) with at most 6 intervals and some headroom.
    niceAxis(lo, hi) {
      const span = Math.max(hi - lo, 1e-6) * 1.2;
      const mag = 10 ** Math.floor(Math.log10(span / 6));
      const step = [1, 2, 5, 10].map((m) => m * mag).find((st) => span / st <= 6);
      const mid = (lo + hi) / 2;
      const min = Math.floor((mid - span / 2) / step) * step;
      const max = Math.ceil((mid + span / 2) / step) * step;
      return { min, max, ticks: Math.round((max - min) / step) };
    },

    unit(p) {
      return this.G_getUOM({ uom: this.consts.UOM[p.uom] });
    },

    // Forecast dates are sensor-local calendar days; plot them at midday.
    noon(isoDate) {
      return new Date(`${isoDate}T12:00:00`).getTime();
    },

    day(isoDate) {
      return moment(isoDate).format('ddd, MMM D');
    },

    // Why the "if not irrigated" line is shown: the forecast's irrigation flag and where it came from.
    irrigationWhy(irr) {
      if (!irr || irr.state !== 'on') {
        return null;
      }
      if (irr.source === 'schedule') {
        return this.$t('sf.irrSchedule');
      }
      if (irr.source === 'person') {
        return irr.set_by_name
          ? this.$t('sf.irrPerson', { name: irr.set_by_name, date: moment(irr.set_at).format('MMM D') })
          : this.$t('sf.irrPersonNoName');
      }
      const ev = irr.evidence || {};
      return this.$t('sf.irrDetected', { n: ev.watering_days, window: ev.window_days, from: this.hourLabel(ev.slot_start_hour) });
    },

    // 22 -> "10 pm" (en) / "22:00" (es); the 3-hour watering slot starts here.
    hourLabel(h) {
      if (h === null || h === undefined) {
        return '';
      }
      return moment({ hour: h }).format(this.$i18n.locale === 'es' ? 'HH:mm' : 'h a');
    },

    // Rain and ET come in mm; imperial courses see inches.
    depth(mm) {
      if (mm === null || mm === undefined) {
        return null;
      }
      return this.imperial ? Number((mm / 25.4).toFixed(2)) : Number(Number(mm).toFixed(1));
    },

    depthUnit() {
      return this.imperial ? 'in' : 'mm';
    },

    // Hourly readings for the measured days shown before the forecast (at most 7).
    async loadHistory() {
      if (!this.sensor) {
        return;
      }
      const sensorId = this.sensor.sensor_id;
      this.loading = true;
      try {
        const rows = await getSensorGraph({ sensorId, alias: 'all', until: 8, resampleFreq: '1h' });
        if (this.sensor && this.sensor.sensor_id === sensorId) {
          this.history = rows || [];
        }
      } catch (e) {
        this.history = [];
      }
      this.loading = false;
    },
  },

  mounted() {
    if (process.env.VUE_APP_SENTRY_ENV == "production") {
      this.$analytics.setCurrentScreen("Web Sensors Forecast");
      this.$analytics.logEvent("web_sensors_forecast");
    }
    this.loadHistory();
    // The tab is mounted while hidden; draw the charts only once it is on screen so ApexCharts
    // measures a real width.
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.visible = true;
        }
      });
      this.observer.observe(this.$el);
    } else {
      this.visible = true;
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>
